import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const ConsultationModal = ({ isOpen, onClose, defaultQueryOption }) => {
    const [formData, setFormData] = useState({
        name: '',
        organisation: '',
        email: '',
        phone: '',
        designation: '',
        queryOption: defaultQueryOption || 'Looking industry specific training solutions',
        description: '',
    });

    const [formStatus, setFormStatus] = useState(null); // 'success', 'error', or null
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && defaultQueryOption) {
            setFormData(prev => ({ ...prev, queryOption: defaultQueryOption }));
        }
    }, [isOpen, defaultQueryOption]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormStatus(null);

        try {
            // Mapping to Apps Script expected keys
            // The user will need to update their Apps Script to handle these new keys
            const payload = {
                fullname: formData.name,
                emailaddress: formData.email,
                organisation: formData.organisation,
                phone: formData.phone,
                designation: formData.designation,
                queryOption: formData.queryOption,
                message: formData.description, // Mapping description to 'message' as a fallback or new field
                subject: `Consultation Request: ${formData.queryOption}`, // Auto-generating subject
            };

            // REPLACE 'YOUR_APPS_SCRIPT_URL' WITH YOUR ACTUAL DEPLOYED SCRIPT URL
            await fetch('https://script.google.com/macros/s/AKfycbyyZIo66_f_N7I5vCPLUVY7iwSCrPDRt8jtUQzJ2HqGhyvyL4y3aqXHx4ENxBav0a9y/exec', {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            setFormStatus('success');
            // Reset form after delay
            setTimeout(() => {
                onClose();
                setFormStatus(null);
                setFormData({
                    name: '',
                    organisation: '',
                    email: '',
                    phone: '',
                    designation: '',
                    queryOption: defaultQueryOption || 'Looking industry specific training solutions',
                    description: '',
                });
            }, 3000);

        } catch (error) {
            console.error("Error submitting form", error);
            setFormStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay">
            <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
          backdrop-filter: blur(4px);
        }

        .modal-content {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
          position: relative;
          padding: 32px;
          animation: modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .modal-header {
          margin-bottom: 24px;
        }

        .modal-title {
          font-family: 'Roboto Slab', serif;
          font-size: 24px;
          font-weight: 700;
          color: #0b3161;
          margin: 0 0 8px 0;
        }

        .modal-subtitle {
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
        }

        .close-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group.full {
          grid-column: 1 / -1;
        }

        label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 6px;
        }

        input, select, textarea {
          width: 100%;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #0f172a;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
          box-sizing: border-box;
          font-family: inherit;
        }

        input:focus, select:focus, textarea:focus {
          background: #ffffff;
          border-color: #fdd649;
          box-shadow: 0 0 0 3px rgba(253, 214, 73, 0.2);
        }

        textarea {
            resize: vertical;
            min-height: 80px;
        }

        .btn-submit {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #fdd649 0%, #f7c42d 100%);
          color: #071426;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(253, 214, 73, 0.25);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(253, 214, 73, 0.35);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .status-msg {
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 500;
          font-size: 14px;
        }

        .status-msg.success {
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .status-msg.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        @media (max-width: 640px) {
           .modal-content { padding: 24px; width: 95%; max-height: 85vh; }
           .form-grid { grid-template-columns: 1fr; gap: 0; }
        }
      `}</style>

            <div className="modal-content" role="dialog" aria-modal="true">
                <button onClick={onClose} className="close-btn" aria-label="Close modal">
                    <X size={20} />
                </button>

                <div className="modal-header">
                    <h2 className="modal-title">Get Expert Consultation</h2>
                    <p className="modal-subtitle">Fill in your details and we'll get back to you shortly.</p>
                </div>

                {formStatus === 'success' && (
                    <div className="status-msg success">
                        <CheckCircle size={18} />
                        <div>Request sent successfully! We will contact you soon.</div>
                    </div>
                )}

                {formStatus === 'error' && (
                    <div className="status-msg error">
                        <AlertCircle size={18} />
                        <div>Something went wrong. Please try again.</div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="name">Full Name *</label>
                            <input
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="John Doe"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="organisation">Organisation Name *</label>
                            <input
                                id="organisation"
                                name="organisation"
                                required
                                value={formData.organisation}
                                onChange={handleChange}
                                placeholder="Company Ltd."
                            />
                        </div>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="john@company.com"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Phone Number *</label>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                required
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+91..."
                            />
                        </div>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="designation">Designation *</label>
                            <input
                                id="designation"
                                name="designation"
                                required
                                value={formData.designation}
                                onChange={handleChange}
                                placeholder="HR Manager"
                            />
                        </div>
                    </div>

                    <div className="form-group full">
                        <label htmlFor="queryOption">I am interested in *</label>
                        <select
                            id="queryOption"
                            name="queryOption"
                            required
                            value={formData.queryOption}
                            onChange={handleChange}
                        >
                            <option value="Looking industry specific training solutions">Looking for Industry Specific Training Solutions</option>
                            <option value="Looking for Acedemic and training parternershipt">Looking for Academic & Training Partnership</option>
                            <option value="Looking for TNI , training need identenfication">Looking for TNI (Training Need Identification)</option>
                            <option value="Other">Other Query</option>
                        </select>
                    </div>

                    <div className="form-group full">
                        <label htmlFor="description">Description (Optional)</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Any specific requirements or questions..."
                        />
                    </div>

                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Sending...
                            </>
                        ) : (
                            'Submit Request'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ConsultationModal;
